import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper
} from '@mui/material';
import './lease-form.scss';
import { format, isValid, parse } from 'date-fns';
import { CLOSING_CLAUSES } from '../../../../../../../leaseClauses/ClosingClauses';

const getOrdinal = (day) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
};

const formatDateWithOrdinal = (dateString) => {
  if (!dateString) return 'Automatically filled when signed';
  const cleanDateStr = dateString.split('T')[0];
  const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
  if (!isValid(date)) return 'Automatically filled when signed';
  const month = format(date, 'MMMM');
  const dayWithOrdinal = getOrdinal(date.getDate());
  const year = format(date, 'yyyy');
  return `${month} ${dayWithOrdinal}, ${year}`;
};

// Blank copy (printed for signing by hand): an empty line to sign/date on, no e-sign wording.
const BlankLine = () => <Box sx={{ height: 24, borderBottom: '1px solid #555' }} />;

const SignatureCell = ({ signature, blank }) => {
  if (blank) return <BlankLine />;
  if (signature) {
    return (
      <Box sx={{ height: 20 }}>
        <img src={signature} alt="Signature" style={{ maxWidth: '100%', maxHeight: '20px' }} />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        height: 20,
        border: '1px solid #eee',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#aaa',
        fontStyle: 'italic',
      }}
    >
      Awaiting signature
    </Box>
  );
};

const DateCell = ({ date, blank }) => (blank ? <BlankLine /> : formatDateWithOrdinal(date));

const TenantDetails = ({ lease, blank }) => (
  <Box className="section">
    <Typography variant="subtitle2" className="section-title">TENANTS AGREEMENT:</Typography>
    <Typography variant="body2" className="section-content" sx={{ mb: 1 }}>
      The tenant agrees that in addition to the occupants, the premises may be occupied only by the following persons:
    </Typography>

    <Paper variant="outlined" className="tenant-details-table" sx={{ mb: 2, borderRadius: 1 }}>
      <Table size="small">
        <TableBody>
          {lease.tenants?.length > 0 ? (
            lease.tenants.map((tenant, index) => (
              <TableRow key={index}>
                <TableCell className="label">Name</TableCell>
                <TableCell className="value">
                  {tenant.name || '-'}
                </TableCell>
                <TableCell className="label">Signature</TableCell>
                <TableCell className="value">
                  <SignatureCell signature={tenant.signature} blank={blank} />
                </TableCell>
                <TableCell className="label">Date</TableCell>
                <TableCell className="value"><DateCell date={tenant.date} blank={blank} /></TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ color: '#aaa' }}>
                No tenants available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  </Box>
);

const LandlordDetails = ({ lease, blank }) => {
  const manager = lease?.manager || {
    first_name: "",
    last_name: "",
    phone_no: "",
    email: "",
    signature: "",
    date: "",
  };
  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">
        LANDLORD/LANDLORD AGENT:
      </Typography>

      <Paper variant="outlined" className="landlord-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label">Name</TableCell>
              <TableCell className="value">
                {`${manager.first_name || ''} ${manager.last_name || ''}`.trim() || '-'}
              </TableCell>
              <TableCell className="label">Signature</TableCell>
              <TableCell className="value">
                <SignatureCell signature={manager.signature} blank={blank} />
              </TableCell>
              <TableCell className="label">Date</TableCell>
              <TableCell className="value"><DateCell date={manager.date} blank={blank} /></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const BuildingAddressDetails = ({ lease }) => {
  const formatAddress = () => {
    const { property_details } = lease;

    if (!property_details) return '';

    const { building_name, address, postal_code, province, city } = property_details;
    const mainAddress = address || building_name;
    const addressParts = [mainAddress, city, province, postal_code];

    return addressParts.filter(Boolean).join(', ');
  };

  const phone = lease.manager?.phone_no || 'N/A';
  const email = lease.manager?.email || 'N/A';

  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">BUILDING ADDRESS AND CONTACT:</Typography>

      <Paper variant="outlined" className="building-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label" sx={{ width: '15%' }}>
                Address:
              </TableCell>
              <TableCell className='value' sx={{ width: '45%' }}>
                <Box sx={{ mb: 1 }}>
                  {formatAddress() || <span style={{ color: '#aaa' }}>No address available</span>}
                </Box>
              </TableCell>
              <TableCell className="label-2" sx={{ width: '15%' }}>Phone & Email:</TableCell>
              <TableCell className='value-2' sx={{ width: '25%' }}>
                <Box sx={{ mb: 0.5 }}>{phone}</Box>
                <Box>{email}</Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const LeaseTermSection = ({ title, content }) => (
  <Box className="section">
    <Typography variant="subtitle2" className="section-title">{title}</Typography>
    <Typography variant="body2" className="section-content">
      {content}
    </Typography>
  </Box>
);

const Step6 = ({ lease, blank = false }) => {

  return (
    <Box className="lease-form-step6">
      {CLOSING_CLAUSES.map((section, index) => (
        <LeaseTermSection
          key={index}
          title={section.title}
          content={section.content}
        />
      ))}

      <Box className="signature">
        <span className="notice">
          IF THERE IS MORE THAN ONE TENANT, THEIR LIABILITY SHALL BE JOINT IN SEVERAL.
        </span>
        <br />
        <span className="notice">
          THE DATE OF THIS AGREEMENT SHALL BE THE DATE THAT THE LAST OF THE PARTIES HAVE SIGNED.
        </span>
        <br />
        <TenantDetails lease={lease} blank={blank} />
        <LandlordDetails lease={lease} blank={blank} />
        <BuildingAddressDetails lease={lease} />
      </Box>
    </Box>
  );
};

export { Step6 };